import React, { useState } from 'react';
import { Order } from '@hero/schema';
import { Button } from '@hero/web-components';
import { RefreshIcon } from '@hero/web-icons';
import { useTranslation } from 'react-i18next';
import { UseOrderAgainConfig } from '@hero/hooks-shared';
import { cn } from '@hero/web-lib';
import { OrderAgainModal } from './OrderAgainModal';

export interface OrderAgainManagerProps {
  /**
   * The order to reorder
   */
  order: Order;

  /**
   * Configuration for the order again hook
   */
  config: UseOrderAgainConfig;

  /**
   * Optional custom trigger component (button/icon/etc.)
   * If not provided, a default trigger will be used based on defaultTrigger prop
   */
  trigger?: React.ReactNode;

  /**
   * Default trigger type if no custom trigger is provided
   * @default 'button'
   */
  defaultTrigger?: 'button' | 'icon';

  /**
   * Display context (customer or carrier)
   * @default 'customer'
   */
  displayForUser?: 'customer' | 'carrier';

  /**
   * Whether to show the order again button based on visibility rules
   * @default true
   */
  showButton?: boolean;

  /**
   * Optional className to extend styling
   */
  className?: string;
}

/**
 * OrderAgainManager - Manages the trigger and modal state for order again functionality
 *
 * This component handles:
 * - Opening/closing the modal
 * - Providing default triggers (button or icon)
 * - Accepting custom triggers
 * - Managing order creation state
 *
 * @example
 * ```tsx
 * // With default button trigger
 * <OrderAgainManager order={order} config={config} />
 *
 * // With custom trigger
 * <OrderAgainManager
 *   order={order}
 *   config={config}
 *   trigger={<Button>Reorder</Button>}
 * />
 *
 * // With default icon trigger
 * <OrderAgainManager
 *   order={order}
 *   config={config}
 *   defaultTrigger="icon"
 * />
 * ```
 */
export const OrderAgainManager: React.FC<OrderAgainManagerProps> = ({
  order,
  config,
  trigger,
  defaultTrigger = 'button',
  displayForUser = 'customer',
  showButton = true,
  className,
}) => {
  const { t } = useTranslation('m-order');
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Enhanced config that closes modal on success
  const enhancedConfig: UseOrderAgainConfig = {
    ...config,
    onSuccess: (createdOrders) => {
      // Close modal on success
      setIsModalOpen(false);
      // Call original onSuccess if provided
      config.onSuccess?.(createdOrders);
    },
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Don't render if showButton is false
  if (!showButton) {
    return null;
  }

  // Render custom trigger if provided
  if (trigger) {
    return (
      <>
        <div onClick={handleOpenModal} className={cn('cursor-pointer', className)}>
          {trigger}
        </div>
        <OrderAgainModal
          order={order}
          displayForUser={displayForUser}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          config={enhancedConfig}
        />
      </>
    );
  }

  // Render default button trigger
  if (defaultTrigger === 'button') {
    return (
      <>
        <Button
          onClick={handleOpenModal}
          className={cn(
            'subtitle2 text-black w-full border rounded-[32px] px-4 py-2 !bg-transparent',
            className,
          )}>
          <span className="flex items-center gap-2">
            <RefreshIcon className="w-5 h-5 text-black" /> {t('order_again')}
          </span>
        </Button>
        <OrderAgainModal
          order={order}
          displayForUser={displayForUser}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          config={enhancedConfig}
        />
      </>
    );
  }

  // Render default icon trigger
  return (
    <>
      <button
        onClick={handleOpenModal}
        className={cn('p-2 hover:bg-gray-100 rounded-full transition-colors', className)}
        aria-label={t('order_again')}>
        <RefreshIcon className="w-5 h-5 text-gray-600" />
      </button>
      <OrderAgainModal
        order={order}
        displayForUser={displayForUser}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        config={enhancedConfig}
      />
    </>
  );
};
